import { FC, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import Transition from 'react-transition-group/Transition';
import { CycleI, PackName } from '~src/interfaces/cycle.interface';
import { SelectionValue } from '~src/interfaces/question.interface';
import { SelectionType, UpdateSelfSelection } from '~src/services/api/pulseCheck';
import { defaultPulseCheckApi } from '~src/services/ApiController';

import logger from '~src/services/logger/LlsLogger';
import { translateContent } from '~src/services/translationServices';
import { Mobile } from '~src/styles/containers.sc';
import CardNavigation from '~src/modules/questions/components/CardNavigation';
import LoadingCard from '~src/modules/questions/components/cards/LoadingCard';
import SelectionCard from '~src/modules/questions/components/cards/SelectionCard';
import MobileTongue from '~src/modules/questions/components/MobileTongue';
import PopoverInfo from '~src/modules/questions/components/PopoverInfo';
import QuestionsInfo from '~src/modules/questions/components/QuestionsInfo';
import { NavigationContainer } from '~src/modules/questions/components/shared.sc';

import { QuestionsGrid, QuestionsWrapper } from './questions.sc';
import { animationSpeed, CardAnimateur } from './stream.sc';

interface OwnProps {
  finishQS: (pack: PackName) => void;
  cycle: CycleI;
  precedent?: string;
}

const SelfSelection: FC<OwnProps> = (props: OwnProps) => {
  const [selfSelection, setSelfSelection] = useState<SelectionType|undefined>(undefined);
  const [isIn, setWantCardShown] = useState<boolean>(false);
  const [isLoadingStream, setIsLoadingStream] = useState<boolean>(true);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);

  useEffect(() => {
    setIsLoadingStream(true);
    const controller = new AbortController();
    defaultPulseCheckApi.withSignal(controller.signal).getSelfSelection(props.cycle.id)
      .then((response) => {
        setSelfSelection(response.data);
        setWantCardShown(true);
        setIsLoadingStream(false);
      })
      .catch((e) => {throw e});

    return () => {
      controller.abort();
    };
  }, []);

  const sendAnswer = (value: SelectionValue) => {
    if (!selfSelection) {
      logger.warn('trying to sendAnswer, but no current selfSelection');
      return;
    }
    const payload: UpdateSelfSelection = {
      about_id: selfSelection.about?.id,
      ask_ids: value.selectedPeople.map((val) => val.id),
      cycle_id: props.cycle.id,
    };
    setIsSaveLoading(true);
    defaultPulseCheckApi.postSelfSelection(payload).then(() => {
      setIsSaveLoading(false);
      selfSelection.asked = value.selectedPeople
      nextCard();
    })
    .catch((error: Error) => {
      logger.info('Error updating SelfSelection', {
        error,
        info: {
          componentStack: 'SelfSelection.tsx',
        },
      });
      throw error;
    }).finally(() => {
        setIsSaveLoading(false);
    });
  };

  /* In the case of Self selection, there is only one Card so every movement will finish the Stream*/
  const nextCard = () => {
    setWantCardShown(false);
    setTimeout(() => {
      props.finishQS(PackName.SelfSelection);
    }, animationSpeed);
  };
  const swipeableHandlers = useSwipeable({
    onSwipedLeft: () => nextCard()
  })
  return (
    <>
      <QuestionsWrapper>
        <QuestionsGrid>
          {!selfSelection || isLoadingStream ? (
            <LoadingCard />
          ) : (
            <>
              <Mobile>
                <PopoverInfo selection={PackName.SelfSelection} />
              </Mobile>
              <Transition in={isIn} timeout={animationSpeed}>
                {(status) => (
                  <CardAnimateur $direction={'left'} className={status}>
                    <div {...swipeableHandlers}>
                      <SelectionCard
                        selectionType={PackName.SelfSelection}
                        currentCard={selfSelection}
                        index={0}
                        onSubmit={sendAnswer}
                        saveLoading={isSaveLoading}
                      />
                    </div>
                  </CardAnimateur>
                )}
              </Transition>
              <NavigationContainer>
                <CardNavigation next={nextCard} max={1} value={1} />
              </NavigationContainer>
              <QuestionsInfo
                count={1}
                dueDate={props.cycle.dueDate}
                selection={PackName.SelfSelection}
                surveyName={translateContent(props.cycle.survey.name)}
              />
            </>
          )}
        </QuestionsGrid>
      </QuestionsWrapper>
      {selfSelection && !isLoadingStream && (
        <MobileTongue
          name={translateContent(props.cycle.survey.name)}
          surveyLength={1}
          dueDate={props.cycle.dueDate}
        />
      )}
    </>
  );
};

export default SelfSelection;
